import type { ETLSourceStatus } from '@/model/etlSource/domain.generated';
import type {
  Action,
  IssueKind,
  Severity,
  Status,
} from '@/model/sourceState/sourcestate.generated';
import { toSourceIssueView } from '@/viewmodel/features/sourceState/mappers/issue';
import { toSourcePhase, toSourceStatusView } from '@/viewmodel/features/sourceState/mappers/status';
import type { SourceStateView } from '@/viewmodel/features/sourceState/types';

interface RawIssue {
  readonly kind: IssueKind;
  readonly action: Action;
  readonly severity: Severity;
}

interface RawVerdict {
  readonly status: Status;
  readonly issue?: RawIssue;
}

function failure(kind: IssueKind, action: Action, severity: Severity = 'error'): RawIssue {
  return { kind, action, severity };
}

const RAW_VERDICT: Record<ETLSourceStatus, RawVerdict> = {
  pending_setup: { status: 'pending_setup' },
  created: { status: 'syncing' },
  ready: { status: 'ready' },
  error: { status: 'failed', issue: failure('source_failed', 'retry_sync') },
  disconnected: { status: 'failed', issue: failure('connection_failed', 'edit_connection') },
  deletion_failed: { status: 'failed', issue: failure('deletion_failed', 'delete_again') },
  disabled: { status: 'disabled' },
  deleting: { status: 'deleting' },
  unrecognized: { status: 'unavailable' },
  stale: { status: 'attention', issue: failure('source_failed', 'retry_sync', 'warning') },
};

function isRawStatus(value: string): value is ETLSourceStatus {
  return value in RAW_VERDICT;
}

export function rawSourceState(raw: string | undefined): SourceStateView {
  const verdict = raw && isRawStatus(raw) ? RAW_VERDICT[raw] : undefined;
  const issue = verdict?.issue;

  return {
    name: undefined,
    status: toSourceStatusView(verdict ? { status: verdict.status, is_stale: false } : undefined),
    phase: toSourcePhase(verdict?.status, issue?.kind, undefined),
    activity: undefined,
    issue: toSourceIssueView(
      issue ? { ...issue, scope: 'source', is_user_actionable: true } : undefined,
    ),
    failedTables: [],
    staleTables: [],
    emptyTables: [],
    rebuildingResults: 0,
  };
}
